import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Rocket, ArrowRight } from 'lucide-react';

const PERKS = [
  'Every feature unlocked',
  'Up to 10 agents',
  'A direct line to the founding team',
  'Your feedback shapes the roadmap',
  'No credit card, no commitment',
];

/**
 * The beta offer.
 *
 * The panel is a plan card, laid out like the one on the pricing page and
 * like every plan card the reader has met elsewhere: a banner tag straddling
 * the top edge, the plan name and its line of description centred under it,
 * the included list, then the figure and the action pinned to the foot.
 * `mt-auto` on that foot is what holds the price and the button on one line
 * if this card is ever set beside a second one.
 *
 * The badge lives on the WRAPPER, not the card — the panel clips its own
 * overflow, which would slice a tag straddling the top edge. Same
 * arrangement as the pricing card, for the same reason.
 *
 * The section now has ONE call to action. It used to have two: a pill under
 * the heading and, once the card grew a foot, a second one a few pixels to
 * its right, both saying claim. The card owns it.
 *
 * The card stands on .card--slate rather than on the amber .card--gradient
 * the rest of this stretch of page uses — one plane, one card, on purpose.
 * Colour comes from that plane, not from this file: it re-points
 * --color-text, --color-text-muted and --color-border for its subtree, so
 * every text-content / text-content-muted in here reverses out to white with
 * no colour utility of its own. The tick is the one thing that does not
 * follow — it keeps its white tile and its --color-art-ok glyph, because a
 * white tick on a dark plane needs the tile to read as a mark rather than as
 * a stray stroke, and --color-success-strong goes pale in dark.
 */
const BetaOfferSection = () => (
  <section className="section section--muted">
    <div className="container-page">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <div>
          <span className="eyebrow accent-3">
            <Rocket className="h-3.5 w-3.5" />
            Beta launch
          </span>

          <h2 className="section-title">
            Use WASLX free
            <br />
            <span className="display-accent">for your first month</span>
          </h2>

          <p className="section-sub max-w-xl">
            We are in beta, which means you get the full product at zero cost — and your feedback
            directly influences what we build next.
          </p>

          <p className="mt-6 text-sm text-content-muted">
            No card needed · Cancel or pause anytime
          </p>
        </div>

        <div className="relative mx-auto w-full max-w-sm pt-3">
          <span className="chip absolute left-1/2 top-0 z-10 -translate-x-1/2 bg-ink text-2xs font-bold uppercase tracking-[0.1em] text-ink-on">
            1 month free
          </span>

          <div className="accent-3 card card--slate flex flex-col !px-6 !pb-6 !pt-8">
            <div className="text-center text-ui-2xl font-bold text-content">Beta access</div>

            <p className="mx-auto mt-2 max-w-[17rem] text-center text-[0.9375rem] leading-relaxed text-content-muted">
              The whole platform, unmetered, for one month from sign-up.
            </p>

            <ul className="mt-8 space-y-2.5">
              {PERKS.map(perk => (
                <li key={perk} className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-pill bg-art-tile text-art-ok">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[0.9375rem] leading-relaxed text-content">{perk}</span>
                </li>
              ))}
            </ul>

            {/* The foot: figure, then action. mt-auto keeps it at the bottom
                however long the list above it runs. */}
            <div className="mt-auto flex flex-col gap-6 pt-10">
              <div className="flex items-end justify-center gap-1">
                <span className="self-start pt-1.5 text-lede font-bold text-content-muted">₹</span>
                <span className="text-hero font-bold leading-[0.85] tabular-nums text-content">0</span>
                <span className="pb-1 text-lede text-content-muted">/ first month</span>
              </div>

              <Link to="/contact" className="btn-pill btn-pill--success btn-pill--block">
                Claim free beta access
                <ArrowRight className="h-[18px] w-[18px]" />
              </Link>
            </div>
          </div>

          <p className="mt-4 text-center text-sm leading-relaxed text-content-muted">
            After the month we share pricing and you decide whether to continue — there are no
            automatic charges.
          </p>
        </div>
      </div>
    </div>
  </section>
);

export default BetaOfferSection;
